import { useLocation, Link } from "react-router-dom";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Home, Search, HelpCircle } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";

const NotFound = () => {
  const location = useLocation();
  const { t } = useTranslation();

  useSEO({
    title: t("notFound.seo.title") || 'Page Non Trouvée - 404 | RAKB',
    description: t("notFound.seo.description") || 'La page que vous recherchez n\'existe pas. Retournez à l\'accueil ou explorez nos véhicules disponibles.',
    noindex: true,
  });

  useEffect(() => {
    if (import.meta.env.DEV) {
      console.error(
        "404 Error: User attempted to access non-existent route:",
        location.pathname
      );
    }
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-white pt-16">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="mb-8">
            <h1 className="text-9xl font-bold text-primary/20 mb-4">404</h1>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t("notFound.title")}
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              {t("notFound.subtitle")}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button asChild size="lg">
              <Link to="/">
                <Home className="mr-2 h-5 w-5" />
                {t("notFound.backHome")}
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/search">
                <Search className="mr-2 h-5 w-5" />
                {t("notFound.searchVehicle")}
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/help">
                <HelpCircle className="mr-2 h-5 w-5" />
                {t("notFound.needHelp")}
              </Link>
            </Button>
          </div>

          <div className="bg-gray-50 rounded-lg p-6 text-left max-w-md mx-auto">
            <h3 className="font-semibold text-gray-900 mb-3">
              {t("notFound.popularPages")}
            </h3>
            <ul className="space-y-2 text-gray-600">
              <li>
                <Link to="/" className="hover:text-primary hover:underline">
                  → {t("notFound.links.home")}
                </Link>
              </li>
              <li>
                <Link to="/search" className="hover:text-primary hover:underline">
                  → {t("notFound.links.search")}
                </Link>
              </li>
              <li>
                <Link to="/how-it-works" className="hover:text-primary hover:underline">
                  → {t("notFound.links.howItWorks")}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-primary hover:underline">
                  → {t("notFound.links.contact")}
                </Link>
              </li>
              <li>
                <Link to="/faq" className="hover:text-primary hover:underline">
                  → {t("notFound.links.faq")}
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
